/**
 * The gadget lifecycle state machine — the thing the product is named for.
 *
 * This lives in the shared package on purpose: the API, the web app and the future mobile app
 * all decide what is permitted from the same table. A second implementation would drift, and
 * the drift would show up as a transition allowed on one client and refused by the server.
 *
 * Nothing here does I/O. Whether a transition is *permitted* is answered here; whether it is
 * *recorded* is the API's job.
 */
export const LIFECYCLE_STATES = [
    'procured', // purchase recorded, not yet in hand
    'in_stock', // received and held, nobody is using it
    'assigned', // allocated to a person or team
    'in_service', // actively in use
    'under_repair', // with a repair partner
    'awaiting_parts', // repair blocked on a part
    'returned', // handed back, awaiting triage
    'retired', // withdrawn from service, not yet disposed of
    'disposed', // sold, recycled or written off — the end
];
/**
 * Permitted transitions. Read as "from → the states it may move to".
 *
 * Two deliberate absences worth stating, because they look like omissions:
 *   * `disposed` goes nowhere. Disposal is the end of the record's life; a gadget that comes
 *     back is a new acquisition, not a resurrection of the old row.
 *   * `retired` cannot return to service. Retirement is a decision someone made and recorded;
 *     undoing it silently would leave the history claiming the gadget was never retired.
 */
export const TRANSITIONS = {
    procured: ['in_stock'],
    in_stock: ['assigned', 'under_repair', 'retired'],
    assigned: ['in_service', 'returned', 'under_repair'],
    in_service: ['assigned', 'returned', 'under_repair'],
    under_repair: ['awaiting_parts', 'in_service', 'in_stock', 'retired'],
    awaiting_parts: ['under_repair', 'retired'],
    returned: ['in_stock', 'under_repair', 'retired'],
    retired: ['disposed'],
    disposed: [],
};
export const INITIAL_STATE = 'procured';
// Equivalent-mutant territory: dropping the typeof guard cannot change the answer, because
// includes() compares with SameValueZero and a non-string never matches a string entry. The
// guard is here to narrow `unknown` for the compiler, so the mutant is suppressed rather than
// chased with a test that could not exist.
export const isLifecycleState = (v) => 
// Stryker disable next-line ConditionalExpression
typeof v === 'string' && LIFECYCLE_STATES.includes(v);
export const canTransition = (from, to) => TRANSITIONS[from].includes(to);
export const isTerminal = (state) => TRANSITIONS[state].length === 0;
export class InvalidTransitionError extends Error {
    from;
    to;
    constructor(from, to) {
        super(TRANSITIONS[from].length === 0
            ? `a ${from} gadget cannot change state again`
            : `cannot go from ${from} to ${to}; permitted: ${TRANSITIONS[from].join(', ')}`);
        this.from = from;
        this.to = to;
        this.name = 'InvalidTransitionError';
    }
}
/** Throws unless the move is permitted. The domain refuses it, not the UI. */
export function assertTransition(from, to) {
    if (!canTransition(from, to))
        throw new InvalidTransitionError(from, to);
}
/**
 * Rebuilds the current state from history alone. The status column on a gadget is a cache of
 * this; if the two ever disagree, the history is right — it is append-only and the column is
 * not.
 */
export function reconstructState(history) {
    // A single pass rather than a sort. Sorting had to copy the array to avoid mutating the
    // caller's, and left the tie rule to whether the engine's sort happened to be stable —
    // which is not a thing to leave to chance in the function that decides what a gadget is.
    // `>=` states the rule outright: when two changes share a timestamp, the later one supplied
    // wins, matching the append order the history table hands back.
    let latest;
    for (const change of history) {
        if (latest === undefined || change.at >= latest.at)
            latest = change;
    }
    return latest?.toState ?? INITIAL_STATE;
}
/** Every ordered pair that is NOT permitted. Used to prove each one is refused. */
export function invalidPairs() {
    const out = [];
    for (const from of LIFECYCLE_STATES) {
        for (const to of LIFECYCLE_STATES) {
            if (!canTransition(from, to))
                out.push([from, to]);
        }
    }
    return out;
}
//# sourceMappingURL=lifecycle.js.map